/**
 * ImageService.ts
 * 
 * 职责：
 * 1. 管理画布上的所有图片元素的数据和状态
 * 2. 提供图片操作的核心方法，如添加、删除和修改图片
 * 3. 处理图片绘制和渲染的逻辑
 * 4. 管理图片属性，如大小、位置和旋转
 * 5. 提供图片元素的碰撞检测功能
 * 6. 负责将图片元素渲染到画布上
 * 7. 提供图片元素的增删改查功能
 * 8. 支持图片滤镜效果和调整
 */

import { Point, ImageData } from '../../types';
import { ViewportService } from './ViewportService';
import { v4 as uuidv4 } from 'uuid';

export class ImageService {
  private ctx: CanvasRenderingContext2D | null = null;
  private images: ImageData[] = [];
  private viewportService: ViewportService | null = null;
  private imageCache: Map<string, HTMLImageElement> = new Map(); // 缓存已加载的图片

  constructor(viewportService?: ViewportService) {
    this.viewportService = viewportService || null;
  }

  // 设置画布上下文
  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  // 设置视口服务
  public setViewportService(viewportService: ViewportService): void {
    this.viewportService = viewportService;
  }

  // 获取视口中心位置
  public getViewportCenter(): Point {
    let x = 0;
    let y = 0;
    
    // 如果视口服务可用，尝试使用视口服务获取中心位置
    if (this.viewportService) {
      try {
        // 获取视口尺寸和位置信息
        const scale = this.viewportService.getScale();
        const offsetX = this.viewportService.getOffsetX();
        const offsetY = this.viewportService.getOffsetY();
        
        // 获取画布尺寸
        let canvasWidth = 800;  // 默认值
        let canvasHeight = 600; // 默认值
        
        if (this.ctx && this.ctx.canvas) {
          canvasWidth = this.ctx.canvas.width;
          canvasHeight = this.ctx.canvas.height;
        }
        
        // 计算视口中心点的画布坐标
        x = (canvasWidth / (2 * scale)) - offsetX;
        y = (canvasHeight / (2 * scale)) - offsetY;
      } catch (error) {
        // 失败时使用默认值
        x = 500;
        y = 300;
      }
    } else {
      // 如果视口服务不可用，使用默认值
      x = 500;
      y = 300;
    }
    
    return { x, y };
  }

  // 获取所有图片
  public getImages(): ImageData[] {
    return this.images;
  }

  // 设置图片数据
  public setImages(images: ImageData[]): void {
    this.images = images;
    
    // 预加载所有图片
    this.preloadImages();
  }

  // 预加载所有图片
  private preloadImages(): void {
    this.images.forEach(image => {
      if (!this.imageCache.has(image.src)) {
        const img = new Image();
        img.src = image.src;
        this.imageCache.set(image.src, img);
      }
    });
  }

  // 添加新图片
  public async addImage(src: string, x: number, y: number): Promise<ImageData | null> {
    try {
      // 创建新图片并加载
      const img = new Image();
      
      // 使用Promise等待图片加载完成
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
      });
      
      // 获取创建时的缩放比例
      const creationScale = this.viewportService ? this.viewportService.getScale() : 1;
      
      // 获取画布尺寸，用于自适应调整图片大小
      let canvasWidth = 800;  // 默认值
      let canvasHeight = 600; // 默认值
      
      if (this.viewportService) {
        const canvasSize = this.viewportService.getCanvasSize();
        if (canvasSize.width > 0 && canvasSize.height > 0) {
          canvasWidth = canvasSize.width;
          canvasHeight = canvasSize.height;
        }
      }
      
      // 图片尺寸系数 - 基础缩放因子
      let sizeFactor = 0.5; // 默认为原始尺寸的50%
      
      // 计算原始图片尺寸（基于当前缩放）
      let rawWidth = img.width / creationScale;
      let rawHeight = img.height / creationScale;
      
      // 如果图片宽度超过画布宽度的70%，进一步缩小
      if (rawWidth > canvasWidth * 0.7) {
        const adaptiveFactor = (canvasWidth * 0.7) / rawWidth;
        sizeFactor = Math.min(sizeFactor, adaptiveFactor);
      }
      
      // 如果图片高度超过画布高度的70%，进一步缩小
      if (rawHeight > canvasHeight * 0.7) {
        const adaptiveFactor = (canvasHeight * 0.7) / rawHeight;
        sizeFactor = Math.min(sizeFactor, adaptiveFactor);
      }
      
      // 对于非常小的图片，不要缩放得太小
      if (rawWidth < 100 && rawHeight < 100) {
        sizeFactor = Math.max(sizeFactor, 0.8);
      }
      
      // 计算最终图片尺寸，应用尺寸系数
      const width = rawWidth * sizeFactor;
      const height = rawHeight * sizeFactor;
      
      // 调整x和y坐标，使图片中心点位于鼠标点击位置
      const adjustedX = x - width / 2;
      const adjustedY = y - height / 2;
      
      // 创建图片数据
      const newImage: ImageData = {
        id: uuidv4(),
        x: adjustedX,
        y: adjustedY,
        width,
        height,
        src,
        originalWidth: img.width,
        originalHeight: img.height,
        aspectRatio: img.width / img.height,
        creationScale,
        rotation: 0
      };
      
      // 添加到图片列表
      this.images.push(newImage);
      
      // 缓存图片对象
      this.imageCache.set(src, img);
      
      // 触发渲染
      this.triggerRender();
      
      return newImage;
    } catch (error) {
      return null;
    }
  }

  // 删除图片
  public deleteImages(indices: number[]): void {
    // 按索引从大到小排序，从后往前删除，避免索引变化
    const sortedIndices = [...indices].sort((a, b) => b - a);
    
    sortedIndices.forEach(index => {
      if (index >= 0 && index < this.images.length) {
        this.images.splice(index, 1);
      }
    });
    
    // 触发渲染
    this.triggerRender();
  }

  // 调整图片大小
  public resizeImage(index: number, newWidth: number, newHeight: number): void {
    if (index >= 0 && index < this.images.length) {
      const image = this.images[index];
      image.width = newWidth;
      image.height = newHeight;
      
      // 触发渲染
      this.triggerRender();
    }
  }

  // 旋转图片
  public rotateImage(index: number, angle: number): void {
    if (index >= 0 && index < this.images.length) {
      const image = this.images[index];
      image.rotation = (image.rotation || 0) + angle;
      
      // 触发渲染
      this.triggerRender();
    }
  }

  // 移动图片
  public moveImage(index: number, dx: number, dy: number): void {
    if (index >= 0 && index < this.images.length) {
      const image = this.images[index];
      image.x += dx;
      image.y += dy;
      
      // 触发渲染
      this.triggerRender();
    }
  }

  // 应用图片滤镜
  public applyFilters(index: number, filters: {
    brightness?: number,
    contrast?: number,
    saturation?: number,
    blur?: number
  }): void {
    if (index >= 0 && index < this.images.length) {
      const image = this.images[index];
      image.filters = { ...image.filters, ...filters };
      
      // 触发渲染
      this.triggerRender();
    }
  }

  // 重置图片滤镜
  public resetFilters(index: number): void {
    if (index >= 0 && index < this.images.length) {
      const image = this.images[index];
      image.filters = undefined;
      
      // 触发渲染
      this.triggerRender();
    }
  }

  // 检查点是否在图片区域内
  public isPointInImage(point: Point, index: number): boolean {
    if (index < 0 || index >= this.images.length || !this.images[index]) {
      return false;
    }
    
    const image = this.images[index];
    
    // 如果有旋转，需要进行更复杂的检测
    if (image.rotation && image.rotation !== 0) {
      // 图片中心点
      const centerX = image.x + image.width / 2;
      const centerY = image.y + image.height / 2;
      
      // 将点转换到以图片中心为原点的坐标系
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      
      // 旋转点的反方向
      const rotation = image.rotation || 0;
      const cos = Math.cos(-rotation);
      const sin = Math.sin(-rotation);
      
      // 旋转后的点坐标
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      
      // 检查点是否在图片矩形内
      return (
        rotatedX >= -image.width / 2 &&
        rotatedX <= image.width / 2 &&
        rotatedY >= -image.height / 2 &&
        rotatedY <= image.height / 2
      );
    } else {
      // 无旋转，直接检查点是否在矩形内
      return (
        point.x >= image.x &&
        point.x <= image.x + image.width &&
        point.y >= image.y &&
        point.y <= image.y + image.height
      );
    }
  }

  // 查找被点击的图片的索引
  public findClickedImageIndex(point: Point): number {
    // 从前往后检查，因为前面的图片显示在下层，新添加的元素应该在前面元素之上
    for (let i = 0; i < this.images.length; i++) {
      if (this.isPointInImage(point, i)) {
        return i;
      }
    }
    
    return -1; // 未找到
  }

  // 绘制所有图片
  public drawAllImages(): void {
    if (!this.ctx) {
      return;
    }
    
    // 获取当前缩放
    const scale = this.viewportService ? this.viewportService.getScale() : 1;
    
    // 打印图片数量和信息
    if (this.images.length > 0) {
    }
    
    // 绘制每个图片
    this.images.forEach((image, index) => {
      try {
        this.drawImage(image, scale);
      } catch (error) {
      }
    });
  }

  // 绘制单个图片
  private drawImage(image: ImageData, scale: number): void {
    if (!this.ctx) {
      return;
    }
    
    // 获取缓存的图片对象，如果没有则加载
    let img = this.imageCache.get(image.src);
    if (!img) {
      img = new Image();
      img.src = image.src;
      this.imageCache.set(image.src, img);
      
      // 如果图片尚未加载完成，设置加载事件
      if (!img.complete) {
        img.onload = () => {
          this.triggerRender();
        };
        img.onerror = (error) => {
        };
        return; // 等待加载完成后再绘制
      }
    }
    
    // 保存当前上下文状态
    this.ctx.save();
    
    // 应用变换：平移到图片中心，旋转，然后平移回原位置
    const centerX = image.x + image.width / 2;
    const centerY = image.y + image.height / 2;
    
    this.ctx.translate(centerX, centerY);
    if (image.rotation) {
      this.ctx.rotate(image.rotation);
    }
    
    // 如果有滤镜，应用滤镜效果
    if (image.filters) {
      // 设置图片滤镜
      const filterStrings = [];
      
      if (image.filters.brightness !== undefined) {
        filterStrings.push(`brightness(${image.filters.brightness}%)`);
      }
      
      if (image.filters.contrast !== undefined) {
        filterStrings.push(`contrast(${image.filters.contrast}%)`);
      }
      
      if (image.filters.saturation !== undefined) {
        filterStrings.push(`saturate(${image.filters.saturation}%)`);
      }
      
      if (image.filters.blur !== undefined) {
        filterStrings.push(`blur(${image.filters.blur}px)`);
      }
      
      if (filterStrings.length > 0) {
        this.ctx.filter = filterStrings.join(' ');
      }
    }
    
    try {
      // 绘制图片，注意坐标需要减去中心点偏移
      this.ctx.drawImage(
        img,
        -image.width / 2,
        -image.height / 2,
        image.width,
        image.height
      );
    } catch (error) {
    }
    
    // 恢复上下文状态
    this.ctx.restore();
  }

  // 触发渲染
  private triggerRender(): void {
    if (this.ctx?.canvas) {
      const event = new CustomEvent('render-required');
      this.ctx.canvas.dispatchEvent(event);
      document.dispatchEvent(event);
    }
  }

  // 从文件上传图片
  public async uploadImageFromFile(file: File, x: number, y: number): Promise<ImageData | null> {
    try {
      // 验证文件是否为图片
      if (!file.type.startsWith('image/')) {
        throw new Error('上传的文件不是图片');
      }
      
      // 创建一个文件URL
      const fileUrl = URL.createObjectURL(file);
      
      // 使用URL添加图片
      const imageData = await this.addImage(fileUrl, x, y);
      
      return imageData;
    } catch (error) {
      return null;
    }
  }
} 